import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Rating,
  Box,
  Card,
  CardContent,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  Lock as LockIcon,
  CheckCircle as CheckCircleIcon,
  Undo as UndoIcon
} from '@mui/icons-material';
import axios from 'axios';
import dayjs from 'dayjs';

const FeedbackView = () => {
  const [feedbacks, setFeedbacks] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    rating_min: '',
    rating_max: ''
  });
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [authDialogOpen, setAuthDialogOpen] = useState(true);
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  const statusMap = {
    pending: { label: '未查看', color: 'warning' },
    reviewed: { label: '已查看', color: 'success' },
    resolved: { label: '已解决', color: 'info' }
  };

  const fetchFeedbacks = async () => {
    try {
      setLoading(true);
      const params = {
        page: page + 1,
        page_size: rowsPerPage,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      };
      
      const response = await axios.get('/api/feedback/list', { params });
      setFeedbacks(response.data.data);
      setTotalCount(response.data.total);
      setError('');
    } catch (err) {
      console.error('获取反馈列表失败:', err);
      setError(err.response?.data?.detail || '获取反馈列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/feedback/stats');
      setStats(response.data);
    } catch (err) {
      console.error('获取统计信息失败:', err);
    }
  };

  useEffect(() => {
    // 检查本地存储中的认证状态
    const authStatus = localStorage.getItem('feedbackViewAuth');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
      setAuthDialogOpen(false);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchFeedbacks();
    }
  }, [page, rowsPerPage, filters, isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
    }
  }, [isAuthenticated]);

  const handlePasswordSubmit = async () => {
    if (!password.trim()) {
      setAuthError('请输入密码');
      return;
    }
    
    setAuthLoading(true);
    setAuthError('');
    
    try {
      const response = await axios.post('/api/feedback/admin/auth', {
        password: password
      });
      
      if (response.data.success) {
        setIsAuthenticated(true);
        setAuthDialogOpen(false);
        setAuthError('');
        // 将认证状态和token保存到本地存储（24小时有效）
        localStorage.setItem('feedbackViewAuth', 'true');
        localStorage.setItem('feedbackViewAuthTime', Date.now().toString());
        localStorage.setItem('feedbackViewToken', response.data.token);
      } else {
        setAuthError(response.data.message || '密码错误，请重试');
        setPassword('');
      }
    } catch (error) {
      console.error('认证失败:', error);
      setAuthError('认证失败，请重试');
      setPassword('');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthDialogOpen(true);
    setPassword('');
    setAuthError('');
    localStorage.removeItem('feedbackViewAuth');
    localStorage.removeItem('feedbackViewAuthTime');
    localStorage.removeItem('feedbackViewToken');
  };

  // 检查认证是否过期（24小时）
  useEffect(() => {
    const authTime = localStorage.getItem('feedbackViewAuthTime');
    if (authTime) {
      const timeDiff = Date.now() - parseInt(authTime);
      if (timeDiff > 24 * 60 * 60 * 1000) { // 24小时
        handleLogout();
      }
    }
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0);
  };

  const handleRefresh = () => {
    fetchFeedbacks();
    fetchStats();
  };

  const updateFeedbackStatus = async (feedbackId, newStatus) => {
    try {
      const response = await axios.put(`/api/feedback/${feedbackId}/status?status=${newStatus}`);
      if (response.data.success) {
        // 刷新数据
        fetchFeedbacks();
        fetchStats();
      }
    } catch (err) {
      console.error('更新状态失败:', err);
      setError(err.response?.data?.detail || '更新状态失败');
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  if (!isAuthenticated) {
    return (
      <Dialog open={authDialogOpen} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon />
          管理员验证
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            请输入管理员密码以查看用户反馈
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="密码"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            error={!!authError}
            helperText={authError}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handlePasswordSubmit();
              }
            }}
            autoFocus
          />
        </DialogContent>
         <DialogActions>
           <Button 
             onClick={handlePasswordSubmit} 
             variant="contained"
             disabled={authLoading}
           >
             {authLoading ? '验证中...' : '验证'}
           </Button>
         </DialogActions>
      </Dialog>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" component="h1">
              用户反馈管理
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={handleRefresh}
              disabled={loading}
            >
              刷新
            </Button>
            <Button 
              variant="outlined" 
              color="secondary" 
              onClick={handleLogout}
              size="medium"
            >
              退出
            </Button>
          </Box>
        </Box>

        {/* 统计卡片 */}
        {stats && (
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    总反馈数
                  </Typography>
                  <Typography variant="h4">
                    {stats.total_count}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    平均评分
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="h4">
                      {stats.avg_rating}
                    </Typography>
                    <Rating value={stats.avg_rating} readOnly precision={0.1} />
                  </Box>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    未查看
                  </Typography>
                  <Typography variant="h4" color="warning.main">
                    {stats.status_stats.pending}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Card>
                <CardContent>
                  <Typography color="textSecondary" gutterBottom>
                    已解决
                  </Typography>
                  <Typography variant="h4" color="success.main">
                    {stats.status_stats.resolved}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}

        {/* 过滤器 */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            筛选条件
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>状态</InputLabel>
                <Select
                  value={filters.status}
                  label="状态"
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                >
                  <MenuItem value="">全部</MenuItem>
                  <MenuItem value="pending">未查看</MenuItem>
                  <MenuItem value="reviewed">已查看</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="最低评分"
                type="number"
                inputProps={{ min: 1, max: 10 }}
                value={filters.rating_min}
                onChange={(e) => handleFilterChange('rating_min', e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="最高评分"
                type="number"
                inputProps={{ min: 1, max: 10 }}
                value={filters.rating_max}
                onChange={(e) => handleFilterChange('rating_max', e.target.value)}
              />
            </Grid>
          </Grid>
        </Paper>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>昵称</TableCell>
                <TableCell>反馈内容</TableCell>
                <TableCell>评分</TableCell>
                <TableCell>状态</TableCell>
                <TableCell>IP地址</TableCell>
                <TableCell>提交时间</TableCell>
                <TableCell>操作</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : feedbacks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="textSecondary">
                      暂无反馈数据
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                feedbacks.map((feedback) => (
                  <TableRow key={feedback.id} hover>
                    <TableCell>{feedback.id}</TableCell>
                    <TableCell>
                      {feedback.nickname || (
                        <Typography variant="body2" color="textSecondary">
                          匿名用户
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Tooltip title={feedback.content} arrow>
                        <Typography variant="body2">
                          {truncateText(feedback.content)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Rating value={feedback.rating} readOnly size="small" />
                        <Typography variant="body2">
                          ({feedback.rating}/10)
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusMap[feedback.status]?.label || feedback.status}
                        color={statusMap[feedback.status]?.color || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="textSecondary">
                        {feedback.ip_address || 'N/A'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {dayjs(feedback.created_at).format('YYYY-MM-DD HH:mm')}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {feedback.status === 'pending' ? (
                          <Tooltip title="标记为已查看">
                            <IconButton 
                              size="small" 
                              color="success"
                              onClick={() => updateFeedbackStatus(feedback.id, 'reviewed')}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="标记为未查看">
                            <IconButton 
                              size="small" 
                              color="warning"
                              onClick={() => updateFeedbackStatus(feedback.id, 'pending')}
                            >
                              <UndoIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="查看详情">
                          <IconButton size="small" color="primary">
                            <VisibilityIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="每页显示："
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} 共 ${count} 条`}
        />
      </Paper>
    </Container>
  );
};

export default FeedbackView;